import type { ReactNode } from "react";

export type Segment = { value: string; label: string; node?: ReactNode };

export type SegmentedControlProps = {
  /** 2–3 options only (per brand.md §5 — beyond that, use a dropdown). */
  options: Segment[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
};

/**
 * Vertrouwd segmented pill — the shared 2–3-option control used by
 * DefaultModeRow, the DUO-default toggle, and the (placeholder) Dagdoel row.
 * Active segment lifts onto a raised --panel chip; idle segments are muted.
 * Colour transitions only (no width/height/margin), ease-out — brand motion rule.
 */
export function SegmentedControl({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: SegmentedControlProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center rounded-full border border-[var(--border)] bg-[var(--panel-soft)] p-1"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            disabled={disabled}
            className={[
              "inline-flex h-9 sm:h-10 min-h-[36px] items-center gap-2 rounded-full px-3 sm:px-4 text-xs sm:text-sm font-semibold",
              "transition-[color,background-color,box-shadow] duration-150 ease-out",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-60",
              active
                ? "bg-[var(--panel)] text-[var(--accent)] shadow-[0_1px_2px_rgba(37,35,31,.08),0_4px_10px_-6px_rgba(37,35,31,.3)]"
                : "text-[var(--ink-muted)] hover:bg-[var(--accent-soft)] hover:text-[var(--ink)]",
            ].join(" ")}
          >
            {opt.node}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
